import { createBrowserRouter, useNavigate, useSearchParams } from "react-router-dom";
import App from "@/App";
import { useGlobalStore } from "@/GlobalStore";
import { useEffect } from "react";
import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { TrialGuard, PermissionGuard } from "./guards";
import { ROUTE_CONST } from "./constants";
import NoAccountsPage from "@/screens/creatives/components/NoAccountsPage";

import CommentAnalysisReport from "@/screens/analysis/components/report/CommentAnalysisReport";
import CompetitorAdsAnalysis from "@/screens/analysis/components/competitor/brands/CompetitorAdsAnalysis";
import CompetitorFolderView from "@/screens/analysis/components/competitor/folders/CompetitorFolderView";
import CompetitorReport from "@/screens/analysis/components/competitorReports";
import CreateDashboard from "@/screens/creative-library/components/tabs/CreateDashboard";
import ExploreDashboard from "@/screens/creative-library/components/tabs/ExploreDashboard";
import WorkflowDashboard from "@/screens/creative-library/components/tabs/WorkflowDashboard";
import PublishDashboard from "@/screens/creative-library/components/tabs/publish/PublishDashboard";
import FolderDashboard from "@/screens/creative-library/components/tabs/FolderDashboard";
import OverviewRoute from "@/screens/creatives/routes/OverviewRoute";
import ReportsRoute from "@/screens/creatives/routes/ReportsRoute";
import useAccountsList from "@/hooks/useAccountsList";
import ExpiredTokenGuard from "./guards/ExpiredTokenGuard";

const LazyCreativeLibrary = lazy(() => import("@/screens/creative-library"));

const LazyAnalyze = lazy(() => import("@/screens/creatives"));
const LazyAcceptInvite = lazy(() => import("@/screens/settings/components/AcceptInvite"));
const LazyProfile = lazy(() => import("@/screens/profile"));
const LazyVideoEditor = lazy(() => import("@/screens/videoEditor/pages/video-editor"));
const LazyCommentAnalysis = lazy(() => import("@/screens/analysis"));
const LazySettings = lazy(() => import("@/screens/settings"));
const LazyTeam = lazy(() => import("@/screens/settings/components/Team/Team"));
const LazyInstructions = lazy(
  () => import("@/screens/settings/components/Instructions/Instructions"),
);
const LazySetup = lazy(() => import("@/screens/settings/components/Setup/Setup"));
const LazyWinningCriteria = lazy(
  () => import("@/screens/settings/components/WinningCriteria/WinningCriteria"),
);
const LazyCreativeOverview = lazy(
  () => import("@/screens/settings/components/CreativeOverview/CreativeOverview"),
);
const LazyLabels = lazy(() => import("@/screens/settings/components/labels"));
const LazyExtension = lazy(() => import("@/screens/settings/components/extension"));
const RootRedirect = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { isInitializingAccounts, noAccountsAvailable, accountList } = useGlobalStore();
  const { handleAccountChange } = useAccountsList();
  useEffect(() => {
    if (!isInitializingAccounts && accountList.length) {
      handleAccountChange(accountList?.[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializingAccounts, navigate]);

  // No account case
  if (noAccountsAvailable)
    return (
      <NoAccountsPage
        onAddAccount={() => {
          setSearchParams({ add_account: "true" });
        }}
      />
    );

  return <></>;
};

export const router = createBrowserRouter([
  {
    path: ROUTE_CONST.ROOT,
    element: <App />,
    children: [
      {
        path: "",
        element: <RootRedirect />,
      },
      {
        path: ROUTE_CONST.INVITE,
        element: <LazyAcceptInvite />,
      },
      {
        path: ROUTE_CONST.PROFILE,
        element: <LazyProfile />,
      },
      {
        path: ROUTE_CONST.VIDEO_EDITOR,
        element: (
          <PermissionGuard requiredPermission="video_editor" keycloakRealm="Insight" />
        ),
        children: [
          {
            path: "",
            element: <LazyVideoEditor />,
          },
        ],
      },
      {
        path: ROUTE_CONST.PARAMS.ACCOUNT_ID,
        element: <TrialGuard />,
        children: [
          {
            path: "",
            element: <Navigate to={ROUTE_CONST.ANALYZE} replace />,
          },
          {
            path: ROUTE_CONST.ANALYZE,
            element: <ExpiredTokenGuard />,
            children: [
              {
                path: "",
                element: <LazyAnalyze />,
                children: [
                  {
                    path: ROUTE_CONST.OVERVIEW,
                    element: <OverviewRoute />,
                  },
                  {
                    path: ROUTE_CONST.REPORTS,
                    element: <ReportsRoute />,
                  },
                  {
                    path: `${ROUTE_CONST.REPORTS}/${ROUTE_CONST.PARAMS.REPORT_ID}`,
                    element: <ReportsRoute />,
                  },
                  {
                    path: ROUTE_CONST.FATIGUE,
                    element: <ReportsRoute />,
                  },
                ],
              },
            ],
          },
          {
            path: ROUTE_CONST.RESEARCH,
            element: <PermissionGuard keycloakRealm="Insight" />,
            children: [
              {
                path: "",
                element: <LazyCommentAnalysis />,
                children: [
                  { path: ROUTE_CONST.COMMENTS, element: <CommentAnalysisReport /> },
                  {
                    path: ROUTE_CONST.COMPETITOR_ADS,
                    element: <CompetitorAdsAnalysis />,
                  },
                  {
                    path: `${ROUTE_CONST.COMPETITOR_ADS}/${ROUTE_CONST.FOLDER}`,
                    element: <CompetitorFolderView />,
                  },

                  {
                    // normal competitor report
                    path: `${ROUTE_CONST.COMPETITOR_REPORTS}/${ROUTE_CONST.PARAMS.REPORT_ID}?`,
                    element: <CompetitorReport />,
                  },
                ],
              },
            ],
          },
          {
            path: ROUTE_CONST.CREATE,
            element: <PermissionGuard keycloakRealm="creative" />,
            children: [
              {
                path: "",
                element: <LazyCreativeLibrary />,
                children: [
                  { path: "", element: <CreateDashboard /> },
                  { path: ROUTE_CONST.WORKFLOW, element: <WorkflowDashboard /> },
                  { path: ROUTE_CONST.EXPLORE, element: <ExploreDashboard /> },
                  { path: ROUTE_CONST.PUBLISH, element: <PublishDashboard /> },
                  { path: ROUTE_CONST.FOLDER, element: <FolderDashboard /> },
                ],
              },
            ],
          },
          {
            path: ROUTE_CONST.SETTINGS,
            element: <PermissionGuard keycloakRealm="Insight" />,
            children: [
              {
                path: "",
                element: <LazySettings />,
                children: [
                  { path: ROUTE_CONST.TEAM, element: <LazyTeam /> },
                  { path: ROUTE_CONST.INSTRUCTIONS, element: <LazyInstructions /> },
                  { path: ROUTE_CONST.SETUP, element: <LazySetup /> },
                  {
                    path: ROUTE_CONST.WINNING_CRITERIA,
                    element: <LazyWinningCriteria />,
                  },
                  {
                    path: ROUTE_CONST.CREATIVE_OVERVIEW,
                    element: <LazyCreativeOverview />,
                  },
                  {
                    path: ROUTE_CONST.LABELS,
                    element: <LazyLabels />,
                  },
                  {
                    path: ROUTE_CONST.EXTENSION,
                    element: <LazyExtension />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
